import Vue from 'vue';
import types from '../mutation-types';
import AiNotesAPI from '../../api/aiNotes';

// Every key is declared up front, uiFlags included: Vue 2 only observes what it walks at store
// creation, so a flag added on first use would flip without a single component re-rendering.
export const state = {
  // conversation id -> the note, or null once we know the conversation has none yet. An id that
  // is absent entirely therefore still means "never asked", which is the only way to tell a
  // first fetch from a conversation that is simply too short to have been summarised.
  records: {},
  // An account without the feature switched on is a setting, not a failure. The first 403
  // records it here and the sidebar drops the section; nothing asks a second time.
  isAvailable: true,
  uiFlags: {
    isFetching: false,
    isRefreshing: false,
  },
};

export const getters = {
  // null for both "no note" and "not fetched": the panel says the same calm sentence either
  // way, and the fetching flag is what separates them while a request is in the air.
  getNote: $state => conversationId => {
    return $state.records[conversationId] || null;
  },
  isAvailable($state) {
    return $state.isAvailable;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async function getAiNote({ commit }, conversationId) {
    commit(types.SET_AI_NOTE_UI_FLAG, { isFetching: true });
    try {
      const response = await AiNotesAPI.get(conversationId);
      commit(types.SET_AI_NOTE, {
        conversationId,
        note: response.data.payload,
      });
    } catch (error) {
      // Swallowed on purpose, and the only error that is. The account was never offered this
      // feature, so there is nothing to tell the agent — only a section to take away.
      if (error.response?.status === 403) {
        commit(types.SET_AI_NOTE_AVAILABILITY, false);
        return;
      }
      throw error;
    } finally {
      commit(types.SET_AI_NOTE_UI_FLAG, { isFetching: false });
    }
  },

  // Rethrown like the fetch above, because a 503 carries the server's own sentence about why
  // the note could not be written. Restating that here would be a second, worse translation.
  refresh: async function refreshAiNote({ commit }, conversationId) {
    commit(types.SET_AI_NOTE_UI_FLAG, { isRefreshing: true });
    try {
      const response = await AiNotesAPI.refresh(conversationId);
      commit(types.SET_AI_NOTE, {
        conversationId,
        note: response.data.payload,
      });
      return response.data.payload;
    } catch (error) {
      if (error.response?.status === 403) {
        commit(types.SET_AI_NOTE_AVAILABILITY, false);
        return null;
      }
      throw error;
    } finally {
      commit(types.SET_AI_NOTE_UI_FLAG, { isRefreshing: false });
    }
  },
};

export const mutations = {
  [types.SET_AI_NOTE_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  // Vue.set, because the id is new to the object on every conversation an agent opens, and a
  // plain assignment would leave the sidebar showing the note of the conversation before it.
  // null is stored rather than skipped: "asked, and there is none" is a fact worth keeping.
  [types.SET_AI_NOTE]($state, { conversationId, note }) {
    Vue.set($state.records, conversationId, note || null);
  },

  [types.SET_AI_NOTE_AVAILABILITY]($state, isAvailable) {
    $state.isAvailable = isAvailable;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
